import { useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useLocation, useNavigate } from "react-router-dom";

import { useCurrentUser } from "@/hooks/useCurrentUser";
import { getUserRole } from "@/utils/users";

import LoadingSpinner from "../../../components/LoadingSpinner";

interface LicenseeSubscriptionGateProps {
  children: React.ReactNode;
}

const LicenseeSubscriptionGate = ({
  children,
}: LicenseeSubscriptionGateProps) => {
  const { user } = useAuth0();
  const { user: currentUser, isLoading } = useCurrentUser();
  const navigate = useNavigate();
  const location = useLocation();

  const userRole = getUserRole(user);

  useEffect(() => {
    // Only check subscription for licensees
    if (userRole === "licensee" && !isLoading && currentUser) {
      const cohortStartMs = currentUser.cohort?.startDate
        ? new Date(currentUser.cohort.startDate).getTime()
        : undefined;
      const beforeCohortStart = cohortStartMs
        ? cohortStartMs > Date.now()
        : false;

      const shouldBeOnWelcome =
        !currentUser.subscriptionActive ||
        (currentUser.subscriptionActive && beforeCohortStart);

      // Redirect to welcome when unpaid, or after payment until cohort starts
      if (shouldBeOnWelcome && location.pathname !== "/welcome") {
        navigate("/welcome", { replace: true });
      }
    }
  }, [userRole, currentUser, isLoading, navigate, location.pathname]);

  // Show loading while checking subscription status
  if (userRole === "licensee" && isLoading) {
    return <LoadingSpinner />;
  }

  // If licensee should be on welcome and is navigating there, don't render content
  if (userRole === "licensee" && currentUser) {
    const cohortStartMs = currentUser.cohort?.startDate
      ? new Date(currentUser.cohort.startDate).getTime()
      : undefined;
    const beforeCohortStart = cohortStartMs
      ? cohortStartMs > Date.now()
      : false;
    const shouldBeOnWelcome =
      !currentUser.subscriptionActive ||
      (currentUser.subscriptionActive && beforeCohortStart);
    if (shouldBeOnWelcome && location.pathname !== "/welcome") {
      return <LoadingSpinner />;
    }
  }

  return <>{children}</>;
};

export default LicenseeSubscriptionGate;
